import { toHexColor } from '@atlassian/color-picker/utils/color-conversion';
import { readableTextColor } from '@atlassian/color-picker/utils/readable-text-color';

import { isAutoManagedTextColor } from './is-auto-managed-text-color';

const HEX = /^#(?:[0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})$/i;

/**
 * Pure text-colour resolver: given what is stored on an element and the hex its
 * background actually resolves to, return the colour to render.
 *
 * Auto-managed values (black / white — see `isAutoManagedTextColor`) are
 * re-derived so they stay legible against the live background. Everything else
 * is returned untouched.
 *
 * `backgroundHex` must already be a real colour. Passing a `var(--hub-...)`
 * string here would send `readableTextColor` through `extractColorCode`, which
 * looks the var up on `document.documentElement` and finds nothing — yielding
 * black and therefore the inverse of the right answer. Callers should resolve
 * through the in-memory palette first; `useHubAutoTextColor` does this.
 */
export const resolveAutoTextColor = ({
	textColor,
	backgroundHex,
	packageName,
}: {
	backgroundHex: string;
	packageName: string;
	textColor: string | undefined;
}): string | undefined => {
	if (!isAutoManagedTextColor(textColor)) {
		return textColor;
	}

	// Only derive when the background actually resolved to a colour we can
	// measure. `readableTextColor` swallows a failure and returns `color.text`,
	// so an unresolvable background would silently flip white text to dark — on
	// a dark surface that is worse than leaving the stored value alone. Happens
	// for a value the palette cannot resolve: an unrecognised token with no
	// fallback hex, or a var read during SSR where there is no computed style.
	if (!HEX.test(toHexColor(backgroundHex).trim())) {
		return textColor;
	}

	return readableTextColor(backgroundHex, packageName);
};
